import { filesToDataUrls } from "@/common/util/file-to-data-url";
import React, { ChangeEvent, useEffect, useState } from "react";
import { ImageCarouselDeleteButton } from "../button/ImageCarouselDeleteButton";
import { ImageCarouselUpdateButton } from "../button/ImageCarouselUpdateButton";
import { ImageFileInput } from "../file-input/ImageFileInput";
import { ArrowLeftlineIcon } from "../icon/ArrowLeftlineIcon";
import { ArrowRightlineIcon } from "../icon/ArrowRightlineIcon";

interface Props {
  files: File[];
  onFileChange: (files: File[]) => void;
}

export const ImageCarouselInput: React.FC<Props> = ({
  files,
  onFileChange,
}) => {
  const [images, setImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const isLeftButtonDisabled = currentIndex === 0 || images.length === 0;
  const isRightButtonDisabled =
    currentIndex === images.length || images.length === 0;

  const handlePrevious = () => {
    if (isLeftButtonDisabled) {
      return;
    }

    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    if (isRightButtonDisabled) {
      return;
    }

    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? images.length : prevIndex + 1
    );
  };

  // 새로운 이미지를 추가하는 함수
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const maxSize = 10 * 1024 * 1024;

      const validFiles = filesArray.filter(
        (file) => file.type.startsWith("image/") && file.size <= maxSize
      );

      if (validFiles.length !== filesArray.length) {
        alert("이미지 파일만 업로드 가능합니다. (최대 10MB)");
      }

      if (validFiles.length > 0) {
        onFileChange([...files, ...validFiles]);
        setCurrentIndex(images.length);
      }
    }
  };

  // 기존 이미지를 업데이트하는 함수
  const handleUpdateFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const maxSize = 10 * 1024 * 1024;

      const validFiles = filesArray.filter(
        (file) => file.type.startsWith("image/") && file.size <= maxSize
      );

      if (validFiles.length !== filesArray.length) {
        alert("이미지 파일만 업로드 가능합니다. (최대 10MB)");
      }

      if (validFiles.length > 0) {
        const newFiles = [...files];
        newFiles[currentIndex] = validFiles[0];

        onFileChange(newFiles);
      }
    }
  };

  const handleDeleteImage = () => {
    const newFiles = files.filter((_, index) => index !== currentIndex);
    onFileChange(newFiles);

    if (currentIndex > newFiles.length) {
      setCurrentIndex(newFiles.length - 1);
    }
  };

  useEffect(() => {
    if (files.length > 0) {
      setImagePreviewAsync();
    } else {
      setImages([]);
    }
  }, [files]);

  const setImagePreviewAsync = async () => {
    try {
      const fileUrls = await filesToDataUrls(files);
      setImages(fileUrls);
    } catch (error) {
      console.error("Error reading files:", error);
      return [];
    }
  };

  return (
    <div className="flex flex-col w-[769px] rounded-[8px] overflow-hidden">
      <div className="flex justify-center items-center relative w-full h-[427px]">
        {currentIndex !== images.length ||
        (images.length > 0 && currentIndex < images.length) ? (
          <>
            <img
              src={images[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              className="w-full h-full object-contain"
            />
            <div className="flex absolute bottom-[16px] left-[16px]">
              <ImageCarouselDeleteButton handleClick={handleDeleteImage} />
              <ImageCarouselUpdateButton
                onFileChange={handleUpdateFileChange}
              />{" "}
              {/* 기존 이미지 변경 */}
            </div>
          </>
        ) : (
          <ImageFileInput onFileChange={handleFileChange} />
        )}
      </div>
      <div className="flex justify-center gap-[16px] mt-[24px]">
        <button
          onClick={handlePrevious}
          className={isLeftButtonDisabled ? "cursor-default" : "cursor-pointer"}
          disabled={images.length === 0}
        >
          <ArrowLeftlineIcon
            size={32}
            color={isLeftButtonDisabled ? "text-gray-400" : "text-gray-900"}
          />
        </button>
        <button
          onClick={handleNext}
          className={
            isRightButtonDisabled ? "cursor-default" : "cursor-pointer"
          }
          disabled={images.length === 0 || isRightButtonDisabled}
        >
          <ArrowRightlineIcon
            size={32}
            color={isRightButtonDisabled ? "text-gray-400" : "text-gray-900"}
          />
        </button>
      </div>
    </div>
  );
};
